// ========================
// File: RouteBuilderDetail.js
// ========================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../../services/api';
import RouteInfoModal from './RouteInfoModal';
import styles from './RouteBuilderDetail.module.css';

function RouteBuilderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];

  const [route, setRoute] = useState(null);
  const [routeReceipts, setRouteReceipts] = useState([]);

  // количество единиц с gtin '001' и их общий вес
  const getVolumeStats = receipts => {
    let count = 0;
    receipts.forEach(r => {
      (r.products || []).forEach(p => {
        if (p.gtin === '04600840744001') {
          count += (p.actual_quantity || 0);
        }
      });
    });
    const weight = count * 19; // кг
    return { count, weight };
  };

  // в маршруте хранятся capacity:
  // route.delivery_volume и route.delivery_weight
  const { count: usedVol, weight: usedWeight } = getVolumeStats(routeReceipts);
  const totalVol = Number(route?.delivery_volume) || 0;
  const totalWeight = Number(route?.delivery_weight) || 0;
  // проценты (защита от деления на ноль)
  const volPct = totalVol ? Math.min(100, (usedVol / totalVol) * 100) : 0;
  const weightPct = totalWeight ? Math.min(100, (usedWeight / totalWeight) * 100) : 0;

  const [availableReceipts, setAvailableReceipts] = useState([]);
  const [activeTab, setActiveTab] = useState('inRoute');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [districtFilter, setDistrictFilter] = useState('');
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const formatDate = isoString => {
    const d = new Date(isoString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

    // 1) Функция загрузки свободных квитанций из API
  const fetchAvailableReceipts = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      // res уже — это response.data благодаря интерсептору
      const res = await api.get('/receipts', {
        params: {
          startDate,
          endDate,
          available: true,   // только незанятые
        },
      });
      console.log('fetchAvailableReceipts result:', res);
      // Если вернулся массив — используем его, иначе берём res.receipts
      const receipts = Array.isArray(res) ? res : (res.receipts || []);
      setAvailableReceipts(receipts);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const routeData = await api.get(`/routes/${id}`);
      setRoute(routeData);
      setRouteReceipts(routeData.receipts || []);
      // сразу подтягиваем доступные квитанции
      await fetchAvailableReceipts();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };


  // 1) При монтировании компонента
  useEffect(() => {
    fetchData();
  }, [id]);

  // 2) Перезагружать свободные квитанции при смене таба или фильтров
  // a) при изменении фильтров на вкладке "Свободные"
  useEffect(() => {
    if (activeTab === 'available') {
      fetchAvailableReceipts();
    }
  }, [startDate, endDate, districtFilter]);

  // b) при переключении таба подгружаем данные
  useEffect(() => {
    if (activeTab === 'inRoute') {
      fetchData();
    } else {
      fetchAvailableReceipts();
    }
  }, [activeTab]);



  // Ограничение диапазона семью днями
  useEffect(() => {
    const sd = new Date(startDate);
    const ed = new Date(endDate);
    const diff = (ed - sd) / (1000 * 60 * 60 * 24);
    if (diff > 6) {
      const newEd = new Date(sd);
      newEd.setDate(sd.getDate() + 6);
      setEndDate(newEd.toISOString().split('T')[0]);
    }
  }, [startDate]);

  useEffect(() => {
    const sd = new Date(startDate);
    const ed = new Date(endDate);
    const diff = (ed - sd) / (1000 * 60 * 60 * 24);
    if (diff > 6) {
      const newSd = new Date(ed);
      newSd.setDate(ed.getDate() - 6);
      setStartDate(newSd.toISOString().split('T')[0]);
    }
  }, [endDate]);

  // Добавление квитанции в маршрут
  const attachReceipt = async (receiptId) => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/routes/${id}/receipts/${receiptId}`);
      const receipt = availableReceipts.find(r => r.id === receiptId);
      setRouteReceipts(prev => [...prev, receipt]);
      setAvailableReceipts(prev => prev.filter(r => r.id !== receiptId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Удаление квитанции из маршрута
  const detachReceipt = async (receiptId) => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/routes/${id}/receipts/${receiptId}`);
      const receipt = routeReceipts.find(r => r.id === receiptId);
      setAvailableReceipts(prev => [...prev, receipt]);
      setRouteReceipts(prev => prev.filter(r => r.id !== receiptId));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация доступных квитанций
  const filterAvailable = () => {
    return availableReceipts
      // фильтр по району
      .filter(r => !districtFilter || r.district === districtFilter)
      // фильтр по дате в поле delivery_date
      .filter(r =>
        r.delivery_date >= startDate &&
        r.delivery_date <= endDate
      );
  };

  const districts = Array.from(new Set(availableReceipts.map(r => r.district)));

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <button onClick={() => navigate(-1)} className={styles.back}>
            ← Назад
          </button>
          <button onClick={() => setInfoModalOpen(true)} className={styles.edit}>
            ✏️
          </button>
        </div>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>Маршрут {route?.route_number || id}</h1>
<div className={styles.progressBars}>
  <div className={styles.progressLabel}>
    19л: {usedVol} / {totalVol}
  </div>
  <div className={styles.progress}>
  <div
    className={`${styles.progressFill} ${usedVol > totalVol ? styles.overfill : ''}`}
    style={{ width: `${volPct}%` }}
  />
  </div>

  <div className={styles.progressLabel}>
    Вес: {usedWeight} кг / {totalWeight} кг
  </div>
  <div className={styles.progress}>
    <div
      className={`${styles.progressFill} ${usedWeight > totalWeight ? styles.overfill : ''}`}
      style={{ width: `${weightPct}%` }}
  />
  </div>
</div>
          <div className={styles.metaRow}>
            <span>Дата: {route?.route_date ? formatDate(route.route_date) : '—'}</span>
            <span>Время: {route?.time_from || '—'} – {route?.time_to || '—'}</span>
          </div>
          <div className={styles.metaRow}>
            <span>Водитель: {route?.driver || '—'}</span>
            <span>Грузчик: {route?.loader1 || '—'}</span>
            <span>Грузчик2: {route?.loader2 || '—'}</span>
          </div>
        </div>
      </header>

      <nav className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'inRoute' ? styles.active : ''}`}
          onClick={() => setActiveTab('inRoute')}
        >
          В маршруте ({routeReceipts.length})
        </button>
     <button
       className={`${styles.tab} ${activeTab === 'available' ? styles.active : ''}`}
       onClick={() => setActiveTab('available')}
     >
       Свободные ({filterAvailable().length})
     </button>
      </nav>

      {activeTab === 'available' && (
        <section className={styles.filterRow}>
          <label className={styles.filterLabel}>
            С
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className={styles.dateInput}
            />
          </label>
          <label className={styles.filterLabel}>
            По
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className={styles.dateInput}
            />
          </label>
          <label className={styles.filterLabel}>
            Район
            <select
              value={districtFilter}
              onChange={e => setDistrictFilter(e.target.value)}
              className={styles.selectInput}
            >
              <option value="">Все</option>
              {districts.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>
        </section>
      )}

      <main className={styles.list}>
        {(activeTab === 'inRoute' ? routeReceipts : filterAvailable()).map(r => (
          <div key={r.id} className={styles.card}>
            <div className={styles.cardInfo}>
              <div className={styles.number}>{r.receipt_number}</div>
              <div className={styles.delivery_address}>{r.delivery_address}</div>
              <div className={styles.district}>{r.district}</div>
              {activeTab === 'available' && (
                <div className={styles.date}>{r.date}</div>
              )}
              {/* Новый блок товаров */}
              {r.products && r.products.length > 0 && (
                <ul className={styles.productList}>
                  {r.products.map(product => (
                    <li key={product.id} className={styles.itemRow}>
                      <span className={styles.productName}>{product.product_name}</span>
                      <span className={styles.productQty}>{product.actual_quantity} шт.</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {activeTab === 'inRoute' ? (
              <button
                onClick={() => detachReceipt(r.id)}
                className={`${styles.actionBtn} ${styles.remove}`}
                disabled={loading}
                
              >
                Удалить
              </button>
            ) : (
              <button
                onClick={() => attachReceipt(r.id)}
                className={`${styles.actionBtn} ${styles.add}`}
                disabled={loading}
              >
                Добавить
              </button>
            )}
          </div>
        ))}
        {!loading && (activeTab === 'inRoute'
          ? routeReceipts.length === 0
          : filterAvailable().length === 0) && (
          <div className={styles.empty}>Квитанций нет</div>
        )}
      </main>

      {infoModalOpen && (
        <RouteInfoModal
          routeId={id}
          initialData={route}               // передаём текущие данные при желании
          onClose={() => setInfoModalOpen(false)}
          onSave={updatedRoute => {
            setRoute(updatedRoute);         // обновляем состояние родителя
          }}
        />
      )}

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

export default RouteBuilderDetail;
